import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";

import { AuthProvider, useSoftphoneEnabled } from "./providers/Auth";
import { SoftphoneProvider, CallOverlay } from "softphone";

import config from "./config/config";
import { IntercomProvider } from "react-use-intercom";
import { RouterProvider } from "./providers/Routes";
import { ErrorBoundary } from "providers/ErrorBoundary";
import { ProgressProvider } from "providers/ProgressContext";
import { BulkBrandingProgressProvider } from "providers/BulkBrandingProgress";
import { BulkBrandingProgressPanel } from "providers/BulkBrandingProgress/BulkBrandingProgressPanel";
import { BulkCustomButtonLinkProgressProvider } from "providers/BulkCustomButtonLinkProgress";
import { BulkCustomButtonLinkProgressPanel } from "providers/BulkCustomButtonLinkProgress/BulkCustomButtonLinkProgressPanel";
import { BulkUnitInstructionsProgressProvider } from "providers/BulkUnitInstructionsProgress";
import { BulkUnitInstructionsProgressPanel } from "providers/BulkUnitInstructionsProgress/BulkUnitInstructionsProgressPanel";
import { LeadBulkActionProgressProvider } from "providers/LeadBulkActionProgress";
import { LeadBulkActionProgressPanel } from "providers/LeadBulkActionProgress/LeadBulkActionProgressPanel";
import { Slide, ToastContainer } from "react-toastify";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import "react-toastify/dist/ReactToastify.css";
import { MediaQueryProvider } from "shared/providers";
import { NotificationProvider } from "./providers/NotificationProvider";
import { StepUpAuthProvider } from "./providers/StepUpAuth";

import "react-data-grid/lib/styles.css";
import { NavCustomizationProvider } from "providers/Layout/NavCustomizationProvider";

const INTERCOM_APP_ID = config.intercomId!;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false
    }
  }
});

// Gates the softphone registration on voice capability (CRM-811) — we only mint a
// Twilio capability token when the partner has BoomCRM + provisioned numbers and the
// user can place OR receive calls (CRM-1169). On the login page / post-logout this is
// false.
function AuthGatedSoftphone({ children }: { children: React.ReactNode }) {
  const isSoftphoneEnabled = useSoftphoneEnabled();
  return (
    <SoftphoneProvider enabled={isSoftphoneEnabled}>
      {children}
      <CallOverlay />
    </SoftphoneProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <IntercomProvider appId={INTERCOM_APP_ID}>
        <PostHogProvider client={posthog}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <StepUpAuthProvider>
                <AuthGatedSoftphone>
                  <ProgressProvider>
                    <BulkBrandingProgressProvider>
                      <BulkCustomButtonLinkProgressProvider>
                        <BulkUnitInstructionsProgressProvider>
                          <LeadBulkActionProgressProvider>
                            <MediaQueryProvider>
                              <NotificationProvider>
                                <NavCustomizationProvider>
                                  <NuqsAdapter>
                                    <RouterProvider />
                                  </NuqsAdapter>
                                </NavCustomizationProvider>
                                <BulkBrandingProgressPanel />
                                <BulkCustomButtonLinkProgressPanel />
                                <BulkUnitInstructionsProgressPanel />
                                <LeadBulkActionProgressPanel />
                              </NotificationProvider>
                            </MediaQueryProvider>
                          </LeadBulkActionProgressProvider>
                        </BulkUnitInstructionsProgressProvider>
                      </BulkCustomButtonLinkProgressProvider>
                    </BulkBrandingProgressProvider>
                  </ProgressProvider>
                </AuthGatedSoftphone>
              </StepUpAuthProvider>
            </AuthProvider>
          </QueryClientProvider>
          <ToastContainer
            limit={3}
            style={{
              padding: "16px",
              borderRadius: "8px",
              fontSize: "14px"
            }}
            transition={Slide}
            className="toast-container no-print"
            hideProgressBar
            closeButton={false}
            closeOnClick
            position="bottom-center"
            autoClose={2500}
            theme="colored"
          />
        </PostHogProvider>
      </IntercomProvider>
    </ErrorBoundary>
  );
}

export default App;
